import React, { useContext, useEffect, useState } from "react";
import ChangeRuleModal from "./ChangeRuleModal";
import NewRuleModal from "./NewRuleModal";
import { toast } from "react-toastify";
import AppContext from "../../../context/AppContext";
import CommonButton from "../../common/CommonButton";
import { assignAll, getAllSpecialAssign } from "../../../service/Configuration";
import { CiEdit } from "react-icons/ci";
import UpdateRuleModal from "./UpdateRuleModal";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import translations from "../../common/Translations";

const AssignmentTable = () => {
  const [open, setOpen] = useState(false);
  const [updateRule, setUpdateRule] = useState(null);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [newRule, setNewRule] = useState(false);
  const [specialAssign, setSpecialAssign] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const { activityId, theme ,langMode} = useContext(AppContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const special_assign_header = [
    translations[langMode].number, translations[langMode].FieldAgentName, translations[langMode].geoCodes, translations[langMode].typeCodes, translations[langMode].amountRange, translations[langMode].status, translations[langMode].Action
  ];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = specialAssign.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(specialAssign.length / itemsPerPage);

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const deduplicateByFields = (arr) => {
    const seen = new Set();
    return arr.filter(item => {
      const key = JSON.stringify({
        userNames: item.userNames?.sort(),
        areaCodes: item.areaCodes?.sort(),
        typeCodes: item.typeCodes?.sort(),
        outstandingLabels: item.outstandingLabels?.sort()
      });
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const fetchAllSpecialAssign = async () => {
    try {
      const data = await getAllSpecialAssign(activityId, 0, 100);
      const uniqueRules = deduplicateByFields(data?.ruleList || []);
      setSpecialAssign(uniqueRules);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching special assignments:", error);
    }
  };

  const handleAssignAll = async () => {
    try {
      const data = await assignAll(activityId);
      if (data) {
        toast.success(data.body.value);
        fetchAllSpecialAssign();
      } else {
        toast.error("Failed to assign all rule");
      }
    } catch (error) {
      console.error("Error assigning all rules:", error);
    }
  };

  const handleClick = () => {
    setNewRule(true);
    toast.info("On click, the old assignment rule will be disabled");
  };

  useEffect(() => {
    if (activityId) {
      fetchAllSpecialAssign();
    } else {
      setSpecialAssign([]);
    }
  }, [activityId]);

  return (
    <div className="flex flex-col gap-[12px]">
      <div className="flex justify-between items-center">
        <CommonButton className="rounded-sm" onClick={handleAssignAll} variant="success">
          {translations[langMode].assignAll}
        </CommonButton>
      </div>

      <div className="flex flex-col">
        <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 inline-block min-w-full sm:px-6 lg:px-8">
            <div className="h-[70vh] overflow-auto">
              <table className={`w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400 ${
                    theme === "dark" 
                      ? "dark-mode" 
                      : theme === "high-contrast"
                      ? "high-contrast" 
                      : ""
                }`}>
                <thead className={`text-sm sticky top-0 ${
                    theme === "dark" 
                      ? "dark-mode border-b bg-gray-800 text-gray-300" 
                      : theme === "high-contrast" 
                      ? "high-contrast bg-gray-100" 
                      : "bg-gray-100 text-gray-800"
                  }`}>
                  <tr>
                    {special_assign_header.map((header, inx) => (
                      <th key={inx} scope="col"
                        className="text-base font-bold px-6 py-4 text-left whitespace-nowrap">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item, index) => (
                      <tr
                        className={`${
                          theme === "dark" 
                            ? "dark-mode bg-gray-700 border-gray-600" 
                            : theme === "high-contrast" 
                            ? "high-contrast bg-white" 
                            : "bg-white"
                        } border-b`}
                        key={item.id || index}
                        onClick={() => setSelectedItem(item)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-base font-normal">
                        {indexOfFirstItem + index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-base font-normal">
                          {Array.isArray(item.userNames) && item.userNames.length > 0
                            ? item.userNames.map((name, i) => (
                              <React.Fragment key={i}>
                                {name}
                                {(i + 1) % 3 === 0 && i !== item.userNames.length - 1 ? <br /> : ', '}
                              </React.Fragment>
                            ))
                            : "-"}
                        </td>

                        <td className="text-base font-normal px-6 py-4 whitespace-normal">
                          {item.areaCodes?.map((code, i) => (
                            <React.Fragment key={i}>
                              {code}
                              {(i + 1) % 3 === 0 && i !== item.areaCodes.length - 1 ? <br /> : ', '}
                            </React.Fragment>
                          ))}
                        </td>

                        <td className="text-base font-normal px-6 py-4 whitespace-normal">
                          {item.typeCodes?.map((code, i) => (
                            <React.Fragment key={i}>
                              {code}
                              {(i + 1) % 3 === 0 && i !== item.typeCodes.length - 1 ? <br /> : ', '}
                            </React.Fragment>
                          ))}
                        </td>

                        <td className="text-base font-normal px-6 py-4 whitespace-normal">
                          {item.outstandingLabels?.map((label, i) => (
                            <React.Fragment key={i}>
                              {label}
                              {(i + 1) % 3 === 0 && i !== item.outstandingLabels.length - 1 ? <br /> : ', '}
                            </React.Fragment>
                          ))}
                        </td>

                        <td className="text-base font-normal px-6 py-4 whitespace-nowrap">
                          {item.active === true ? "Active" : "Inactive"}
                        </td>
                        <td className="text-base font-normal px-6 py-4 whitespace-nowrap">
                          <CiEdit
                            className="text-blue-600 cursor-pointer"
                            size={24}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log("Edit clicked:", item);
                              localStorage.setItem("selectedRule", JSON.stringify(item));
                              setUpdateRule(item); // <-- Add this line
                              setUpdateOpen(true);
                            }}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-[#00000066] p-4 px-6 whitespace-nowrap text-center">
                        No Data Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="my-4">
        <p className="text-sm text-gray-400 flex items-center gap-4">
          {`${indexOfFirstItem + 1}-${Math.min(indexOfLastItem, specialAssign.length)} of ${specialAssign.length} ${translations[langMode].items}`}
          <IoIosArrowBack
            onClick={goToPreviousPage}
            disabled={currentPage === 0}
            className={`cursor-pointer ${currentPage === 0 ? 'opacity-50' : ''}`}
          />
          <IoIosArrowForward
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className={`cursor-pointer ${currentPage === totalPages ? 'opacity-50' : ''}`}
          />
        </p>
      </div>

      <div className="sticky bottom-0 left-0 bg-white border-t border-gray-300 z-10 w-full px-6 py-3 flex justify-between">

        <CommonButton className="rounded-sm" onClick={handleClick} variant="primary">
          Add New Rule
        </CommonButton>
      </div>

      {selectedItem && (
        <ChangeRuleModal
          open={open}
          setOpen={setOpen}
          selectedItem={selectedItem}
          setSpecialAssign={setSpecialAssign}
        />
      )}
      <NewRuleModal
        newRule={newRule}
        setNewRule={setNewRule}
        setSpecialAssign={setSpecialAssign}
      />
      <UpdateRuleModal
        updateOpen={updateOpen}
        setUpdateOpen={setUpdateOpen}
        updateRule={updateRule}
        setUpdateRule={setUpdateRule}
        setSpecialAssign={setSpecialAssign}
      />
    </div>
  );
};

export default AssignmentTable;
